import { ampli } from '@/ampli';
import { MouseEventHandler, useEffect, useState } from 'react';
import { useMedia } from 'react-use';

import { NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { breakpoints } from '@/styles/util';

import { CheckCircleIcon } from '@/ui/Icons/CheckCircleIcon';
import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { TokenResult } from '../../../utils/0x/token-registry.types';
import { formatShortAddress } from '../../../utils/address';

const TWO_SECONDS = 2000;

interface Props {
  token: TokenResult | undefined;
  className?: string;
}
export const TokenProfileContractAddress = ({ token, className }: Props) => {
  const [isCopied, setIsCopied] = useState(false);
  const [showCopyAddressTooltip, setShowCopyAddressTooltip] = useState(false);
  const isMobile = useMedia(breakpoints.mobile, false);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    if (isCopied) {
      timeout = setTimeout(() => {
        setShowCopyAddressTooltip(false);
        setTimeout(() => {
          setIsCopied(false);
        }, 100);
      }, TWO_SECONDS);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isCopied]);

  const handleCopyAddress: MouseEventHandler<HTMLButtonElement> = async (e) => {
    if (!token) return;

    e.preventDefault();
    ampli.tokenAddressCopied({
      token_address: token.address,
      token_chain_id: token.chainId,
      token_chain_name: NETWORK_NAME_PER_CHAIN_ID[token.chainId] || '',
    });
    await navigator.clipboard.writeText(token.address);
    setIsCopied(true);
    if (!isMobile) setShowCopyAddressTooltip(true);
  };

  return (
    <div className={className}>
      {token ? (
        <MatchaTooltip open={showCopyAddressTooltip ? 'desktop' : false}>
          <MatchaTooltipTrigger>
            <button
              className="px-2 transition-colors tablet:hover:text-token-gray-900"
              onClick={handleCopyAddress}
              onMouseOver={() => !isMobile && setShowCopyAddressTooltip(true)}
              onMouseOut={() => !isCopied && setShowCopyAddressTooltip(false)}
            >
              <span className="items-center flex gap-1 pointer-events-none">
                {formatShortAddress(token.address, 6, 4)}
                {/* The tooltip never opens on mobile, so the icon is the tap's only feedback. */}
                {isCopied && <CheckCircleIcon aria-hidden className="size-3.5 shrink-0" />}
              </span>
            </button>
          </MatchaTooltipTrigger>
          <MatchaTooltipContent>{isCopied ? 'Copied' : 'Copy address'}</MatchaTooltipContent>
          <MatchaTooltipPopover sideOffset={9} />
        </MatchaTooltip>
      ) : undefined}
    </div>
  );
};
